// External Libraries
import React from "react";
import Modal from "react-responsive-modal";
import { Loader } from "react-feather";

// Services
import ConfigurationService from "../../../services/ConfigurationService";
import PlaidService from "../../../services/PlaidService";
import BankReconciliationService from "../../../services/BankReconciliationService";
import UtilityService from "../../../services/UtilityService";

// Common Components
import WComponent from "../../common/WComponent";
import SearchableDropdown from "../../common/searchable_dropdown/SearchableDropdown";
import Button from "../../common/button/Button";

// Styles
// import "../common/modal/modal.css";
import "./plaid-connection-modal.css";

const { alert } = UtilityService;

class PlaidConnectionModal extends WComponent {
	constructor() {
		super();
		this.state = {
			clientId: null,
			status: null,
			connectionLink: "",
			accounts: [],
			plaidLinkPublicId: "",

			selectedContact: { name: "" },

			loading: false,
			show: false
		};
	}

	componentDidMount() {
		this.resetComponent();
	}

	componentDidUpdate(prevProps) {
		let { clientId, show } = this.props;

		if (prevProps.clientId !== clientId || prevProps.show !== show) {
			this.resetComponent();
		}
	}

	async resetComponent() {
		let { clientId, show } = this.props;

		if (!show) {
			return;
		}

		await this.update({
			show,
			loading: true,
			clientId,
			content: "",

			selectedContact: { name: "" }
		});

		await this.fetchPlaidIntegration();

		await this.update({
			loading: false
		});
	}

	fetchPlaidIntegration = async () => {
		let { clientId } = this.props;

		let plaidLink = await PlaidService.fetchLinks({ clientId });

		if (!plaidLink) {
			await this.update({
				status: null
			});
			return;
		}

		let connectionLink = `${ConfigurationService.resolveApi().clientPortal}connect/${plaidLink.public_id}`;

		if (plaidLink.status === "active") {
			await this.update({
				status: plaidLink.status,
				connectionLink
			});

			return;
		}

		// Default case is that the plaidLink is completed
		let accounts = [];
		let healthStatus = { health: "unknown" };
		let qboAccounts = [];

		try {
			accounts = await PlaidService.fetchAccounts({ clientId });
		} catch (e) {
			console.log(e);
		}

		if (plaidLink.status === "completed") {
			healthStatus = await PlaidService.checkHealth({ clientId });

			try {
				qboAccounts = await BankReconciliationService.fetchBankAccounts({ clientId });
			} catch (e) {
				console.log(e);
			}
		}

		await this.update({
			status: plaidLink.status,
			connectionLink,
			plaidLinkPublicId: plaidLink.public_id,
			accounts,
			qboAccounts,
			connectionHealth: healthStatus.health || plaidLink.connection_health || "unknown",
			errorCode: healthStatus.errorCode || null,
			errorMessage: healthStatus.errorMessage || null,
			lastSuccessfulSync: healthStatus.lastSuccessfulSync || null,
			reconnecting: false,
			autoReconcile: plaidLink.auto_reconcile || false,
			reconPlaidAccountId: plaidLink.recon_plaid_account_id || "",
			reconQboAccountId: plaidLink.recon_qbo_account_id || "",
			savingAutoRecon: false
		});
	};

	show() {
		this.update({
			show: true
		});
	}

	onClose = async confirmed => {
		await this.update({
			show: false
		});

		if (this.props.onClose) {
			this.props.onClose();
		}
	};

	onGenerateConnectionLinkClicked = async () => {
		let { clientId } = this.props;

		await this.update({
			loading: true
		});

		let plaidLink = await PlaidService.createPlaidShortLink({ clientId });

		if (!plaidLink) {
			//error
			return;
		}

		this.fetchPlaidIntegration();

		await this.update({
			loading: false
		});
	};

	doesPlaidLinkExist() {
		let { status } = this.state;
		return status !== null;
	}

	renderAccounts() {
		let { accounts } = this.state;

		return (
			<div className="pcm__accounts">
				Accounts:
				{accounts.map(account => {
					return <div className="pcm__accounts__item">{account.name}</div>;
				})}
			</div>
		);
	}

	renderNone() {
		let { status } = this.state;

		if (status) {
			return null;
		}

		return <div className="pcm__status">Generate a new connection link to share with the client</div>;
	}

	renderActive() {
		let { status, connectionLink } = this.state;

		if (status !== "active" && status !== "processing") {
			return null;
		}

		return (
			<>
				<div className="pcm__status">A connection link has been generated for this client, but they have not connected any accounts yet.</div>
				<div className="pcm__link">
					Connection Link: <a href={connectionLink}>{connectionLink}</a>
				</div>
			</>
		);
	}

	onReconnectClicked = async () => {
		let { clientId } = this.props;

		await this.update({ reconnecting: true });

		let plaidLink = await PlaidService.createUpdateLinkToken({ clientId });

		if (!plaidLink || !plaidLink.link_token) {
			alert({ type: "error", text: "Failed to create re-authentication link." });
			await this.update({ reconnecting: false });
			return;
		}

		// Open the update link in a new tab (client portal handles the Plaid widget)
		let updateLink = `${ConfigurationService.resolveApi().clientPortal}connect/${plaidLink.public_id}`;
		window.open(updateLink, "_blank");

		await this.update({ reconnecting: false });
		alert({ type: "success", text: "Re-authentication link opened. Refresh after the client completes it." });
	};

	renderHealthBadge() {
		let { connectionHealth, errorCode, errorMessage } = this.state;

		if (!connectionHealth || connectionHealth === "unknown") {
			return null;
		}

		const healthConfig = {
			healthy: { label: "Connected", className: "pcm__health--healthy" },
			degraded: { label: "Connection Issue", className: "pcm__health--degraded" },
			disconnected: { label: "Disconnected", className: "pcm__health--disconnected" }
		};

		const config = healthConfig[connectionHealth] || healthConfig.healthy;

		return (
			<div className="pcm__health-container">
				<span className={`pcm__health ${config.className}`}>{config.label}</span>
				{errorMessage && <div className="pcm__health-error">{errorMessage}</div>}
			</div>
		);
	}

	onAutoReconcileToggle = async () => {
		let { autoReconcile } = this.state;
		await this.update({ autoReconcile: !autoReconcile });
	};

	onReconPlaidAccountChange = async e => {
		await this.update({ reconPlaidAccountId: e.target.value });
	};

	onReconQboAccountChange = async e => {
		await this.update({ reconQboAccountId: e.target.value });
	};

	onSaveAutoReconSettings = async () => {
		let { clientId } = this.props;
		let { autoReconcile, reconPlaidAccountId, reconQboAccountId } = this.state;

		await this.update({ savingAutoRecon: true });

		const result = await BankReconciliationService.saveAutoReconSettings({
			clientId,
			autoReconcile,
			plaidAccountId: reconPlaidAccountId || null,
			qboAccountId: reconQboAccountId ? parseInt(reconQboAccountId) : null,
		});

		if (result.error) {
			alert({ type: "error", text: result.error });
		} else {
			alert({ type: "success", text: "Auto-reconciliation settings saved." });
		}

		await this.update({ savingAutoRecon: false });
	};

	renderAutoReconSettings() {
		let { accounts, qboAccounts, autoReconcile, reconPlaidAccountId, reconQboAccountId, savingAutoRecon } = this.state;

		return (
			<div className="pcm__auto-recon">
				<h4>Auto-Reconciliation</h4>
				<div className="pcm__auto-recon-toggle">
					<label>
						<input type="checkbox" checked={autoReconcile} onChange={this.onAutoReconcileToggle} />
						Enable monthly auto-reconciliation
					</label>
				</div>

				{autoReconcile && (
					<div className="pcm__auto-recon-settings">
						<div className="pcm__auto-recon-field">
							<label>Plaid Bank Account</label>
							<select value={reconPlaidAccountId || ""} onChange={this.onReconPlaidAccountChange}>
								<option value="">Select Plaid account</option>
								{accounts.map(account => (
									<option key={account.id} value={account.id}>{account.name}</option>
								))}
							</select>
						</div>

						<div className="pcm__auto-recon-field">
							<label>QBO Bank Account (Books)</label>
							<select value={reconQboAccountId || ""} onChange={this.onReconQboAccountChange}>
								<option value="">Select QBO account</option>
								{qboAccounts.map(account => (
									<option key={account.id} value={account.id}>{account.name}</option>
								))}
							</select>
						</div>

						<Button text={savingAutoRecon ? "Saving..." : "Save Settings"} onClick={this.onSaveAutoReconSettings} disabled={savingAutoRecon} />
					</div>
				)}
			</div>
		);
	}

	renderCompleted() {
		let { status, connectionLink, connectionHealth, reconnecting } = this.state;

		if (status !== "completed") {
			return null;
		}

		return (
			<>
				<div className="pcm__status">This client has connected their bank accounts to Workflow.</div>
				{this.renderHealthBadge()}
				<div className="pcm__link">
					Connection Link: <a href={connectionLink}>{connectionLink}</a>
				</div>
				{this.renderAccounts()}
				{this.renderAutoReconSettings()}
				{(connectionHealth === "disconnected" || connectionHealth === "degraded") && (
					<div className="pcm__reconnect">
						<Button
							text={reconnecting ? "Opening..." : "Reconnect Bank Account"}
							onClick={this.onReconnectClicked}
							disabled={reconnecting}
						/>
					</div>
				)}
			</>
		);
	}

	onContactSelect = async contact => {
		await this.update({
			selectedContact: {
				id: contact.id,
				name: contact.name,
				email: contact.email
			}
		});
	};

	onSendEmail = async () => {
		let { plaidLinkPublicId, selectedContact } = this.state;

		let success = await PlaidService.sendConnectEmail({ plaidLinkPublicId, contactId: selectedContact.id });

		if (!success) {
			alert({ type: "error", text: "Error sending email." });
			return;
		}

		alert({ type: "success", text: `Email has been sent to ${selectedContact.email}` });
	};

	renderEmailSender() {
		let { status, clientId, selectedContact } = this.state;

		if (status !== "active" && status !== "processing") {
			return null;
		}

		return (
			<div>
				<br />
				<br />
				<SearchableDropdown
					containerClass="details__general__input-cont"
					clientId={clientId}
					title="Email Contact"
					type={"contacts"}
					selectedName={selectedContact.name}
					onSelect={this.onContactSelect}
					showAll
				/>
				<Button disabled={!selectedContact.email} text="Send Email" onClick={this.onSendEmail} />
			</div>
		);
	}

	render() {
		let { show, loading } = this.state;

		return (
			<Modal open={show} onClose={() => this.onClose(false)} center>
				<div className="pcm">
					<div className="modal-title">{"Bank Account Details"}</div>

					{loading && (
						<div className="container-loader">
							<Loader />
						</div>
					)}
					{!loading && (
						<div className="pcm__body">
							{this.renderNone()}
							{this.renderActive()}
							{this.renderCompleted()}
							{this.renderEmailSender()}
						</div>
					)}
				</div>
				<div className="modal-footer">
					<Button view="secondary" onClick={() => this.onClose(false)} text="Close" />

					{!this.doesPlaidLinkExist() && <Button onClick={this.onGenerateConnectionLinkClicked} text="Generate Link" />}
				</div>
			</Modal>
		);
	}
}

export default PlaidConnectionModal;
